import { useMemo } from "react";

import { CustomRiskLevel } from "src/core/constants/risk";
import { useFolder } from "src/core/contexts/FolderContext";
import { mapRiskLevelToCustomRiskLevel } from "src/core/mappers/risk.mapper";

export type FolderRiskMap = {
  standard: CustomRiskLevel;
  global: CustomRiskLevel;
  forced: boolean;
  forcedReason: {
    code: string;
    name: string;
  } | null;
};

/**
 * @description Hook pour récupérer tous les risques du dossier.
 * - standard: Risque calculé par L2D Conformité.
 * - global: Correspond au risque standard si le risque n'a pas été modulé par l'utilisateur,
 * sinon au risque modulé.
 * - forced: Indique si le risque a été modulé par l'utilisateur.
 * - forcedReason: Raison de la modulation du risque (sous la forme d'un objet {code: string, name: string}).
 * @returns RiskMap - Tous les risques du dossier @see {@link FolderRiskMap}.
 */
export const useFolderRisks = (): FolderRiskMap => {
  const { folderFile } = useFolder();
  const riskStatus = folderFile?.folderFile?.riskStatus;

  return useMemo(() => {
    return {
      standard: mapRiskLevelToCustomRiskLevel(riskStatus?.standardRiskLevel),
      global: mapRiskLevelToCustomRiskLevel(riskStatus?.globalRiskLevel),
      forced: !!riskStatus?.forcedRisk,
      forcedReason: riskStatus?.forcedRisk?.reason
        ? {
            code: riskStatus?.forcedRisk?.reason?.code,
            name: riskStatus?.forcedRisk?.reason?.name,
          }
        : null,
    };
  }, [riskStatus?.standardRiskLevel, riskStatus?.globalRiskLevel, riskStatus?.forcedRisk]);
};

/**
 *
 * @description Hook pour récupérer un risque spécifique du dossier.
 * @param key - Clé du risque à récupérer @see {@link FolderRiskMap}.
 * @returns RiskMap[key] - Le risque spécifique du dossier.
 */
export const useFolderRisk = <K extends keyof FolderRiskMap>(key: K): FolderRiskMap[K] => {
  const risks = useFolderRisks();
  return risks[key];
};
